export const psychologyOfCommunicationCourse = {
  id: 'psych-comm-101',
  title: 'Psychology of Communication',
  description: 'An exploration of psychological principles underlying human communication, including cognitive processes, emotional factors, social psychology, and behavioral aspects of interpersonal and mass communication.',
  credits: 3,
  duration: '15 weeks',
  level: 'Intermediate',
  category: 'Communications',
  learningObjectives: [
    'Understand psychological theories and principles that explain communication behavior',
    'Analyze cognitive and emotional factors that influence message processing and interpretation',
    'Explore social psychological aspects of group communication and persuasion',
    'Examine the role of personality, motivation, and individual differences in communication',
    'Apply psychological research to improve communication effectiveness and understanding',
    'Evaluate the impact of psychological factors on media consumption and digital communication',
    'Develop skills in therapeutic and supportive communication using psychological principles',
    'Assess cultural and cross-cultural psychological factors affecting communication patterns'
  ],
  prerequisites: ['Introduction to Communication Theory', 'Introduction to Psychology (recommended)', 'Basic statistics knowledge helpful'],
  skills: [
    'Psychological Analysis',
    'Behavioral Assessment',
    'Cognitive Processing Understanding',
    'Emotional Intelligence',
    'Social Psychology Application',
    'Research Interpretation',
    'Critical Thinking',
    'Communication Strategy Development',
    'Empathetic Communication',
    'Conflict Resolution',
    'Cross-Cultural Competence',
    'Data Analysis and Interpretation',
    'Therapeutic Communication Techniques',
    'Persuasion Ethics and Assessment'
  ],
  marketDemand: 'High',
  averageSalary: '$45,000 - $85,000',
  careerPaths: [
    'Communication Consultant',
    'Human Resources Specialist',
    'Organizational Development Manager',
    'Marketing Psychology Specialist',
    'Counseling and Therapy',
    'User Experience Researcher',
    'Training and Development Coordinator',
    'Social Media Psychology Analyst',
    'Corporate Communication Strategist',
    'Employee Assistance Program Coordinator',
    'Customer Experience Designer',
    'Research and Development Specialist'
  ],
  industryApplications: [
    'Healthcare Communication',
    'Corporate Training and Development',
    'Marketing and Advertising',
    'Mental Health Services',
    'Educational Psychology',
    'Digital Media and Technology',
    'Conflict Resolution Services',
    'Cross-Cultural Communication Consulting'
  ],
  certifications: [
    'Certified Communication Professional (CCP)',
    'Human Resource Certification Institute (HRCI)',
    'International Association for the Study of Pain Communication',
    'National Communication Association Certification',
    'Employee Assistance Professional Certification',
    'Cross-Cultural Communication Certification'
  ],
  modules: [
    {
      id: 'module-1',
      title: 'Foundations of Communication Psychology',
      description: 'Introduction to psychological theories and their application to communication processes, covering cognitive and emotional foundations',
      weeks: [1, 2, 3],
      learningOutcomes: [
        'Understand basic psychological theories relevant to communication',
        'Analyze cognitive processes involved in message interpretation',
        'Recognize emotional factors affecting communication effectiveness',
        'Apply personality psychology to communication style analysis'
      ],
      lessons: [
        {
          id: 'lesson-1',
          title: 'Cognitive Processes in Communication',
          content: 'Explore how attention, perception, memory, and thinking processes affect how we encode, transmit, and decode messages. This lesson examines selective attention in communication contexts, perceptual biases that influence message interpretation, memory formation and retrieval in conversational contexts, and cognitive load theory as applied to communication complexity. Students will learn about dual-process theory and its implications for both automatic and controlled communication behaviors.',
          duration: '90 minutes',
          readingMaterials: [
            'Cognitive Psychology and Communication - Chapter 3',
            'Attention and Perception in Message Processing',
            'Research Articles on Cognitive Bias in Communication'
          ],
          keyTerms: [
            'Selective Attention',
            'Perceptual Bias',
            'Cognitive Load',
            'Dual-Process Theory',
            'Schema Theory',
            'Confirmation Bias',
            'Anchoring Effect'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'media-analysis',
              prompt: 'Analyze how different cognitive biases might affect interpretation of a political advertisement. Identify at least three specific biases and explain their psychological mechanisms and impact on message reception.',
              criteria: ['Bias identification accuracy', 'Understanding of cognitive processes', 'Impact analysis depth', 'Clear explanations with examples'],
              timeLimit: 30,
              submissionFormat: 'Written analysis with specific examples'
            }
          }
        },
        {
          id: 'lesson-2',
          title: 'Emotional Factors in Communication',
          content: 'Examine the role of emotions in message encoding, transmission, and decoding processes. This comprehensive lesson covers emotion regulation theory, affective priming in communication, emotional contagion in interpersonal interactions, and the impact of mood states on communication effectiveness. Students will explore how emotional intelligence contributes to communication competence and learn about cultural variations in emotional expression and interpretation.',
          duration: '90 minutes',
          readingMaterials: [
            'Emotion and Communication Theory - Chapters 1-2',
            'Emotional Intelligence in Interpersonal Communication',
            'Cross-Cultural Studies on Emotional Expression'
          ],
          keyTerms: [
            'Emotion Regulation',
            'Affective Priming',
            'Emotional Contagion',
            'Emotional Intelligence',
            'Mood Congruence Effect',
            'Display Rules',
            'Emotional Labor'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'interpersonal-skills',
              prompt: 'Practice identifying and responding to emotional undertones in communication. Role-play a conversation where emotions are high and demonstrate emotional regulation techniques.',
              scenario: 'A colleague is frustrated about a workplace miscommunication that affected an important project deadline',
              timeLimit: 25,
              evaluationCriteria: ['Emotion recognition', 'Appropriate response', 'Emotional regulation demonstration', 'Empathy display']
            }
          }
        },
        {
          id: 'lesson-3',
          title: 'Personality and Communication Styles',
          content: 'Investigate how personality traits influence communication preferences, patterns, and effectiveness. This lesson explores the Big Five personality model and communication behaviors, introversion-extraversion and communication patterns, attachment styles and relationship communication, and cultural personality differences affecting communication norms. Students will learn to assess their own personality-communication connections and develop strategies for adapting communication across different personality types.',
          duration: '90 minutes',
          readingMaterials: [
            'Personality Psychology and Communication Patterns',
            'Big Five Personality Traits in Communication Research',
            'Attachment Theory and Adult Communication'
          ],
          keyTerms: [
            'Big Five Personality Model',
            'Introversion-Extraversion',
            'Attachment Styles',
            'Communication Apprehension',
            'Self-Monitoring',
            'Cognitive Complexity',
            'Locus of Control'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'written-communication',
              prompt: 'Write a comprehensive 750-word analysis of how your personality type affects your communication style, including both strengths and challenges. Include specific examples and strategies for improvement.',
              rubric: 'Evaluate self-awareness depth, personality-communication connections, critical analysis quality, specific examples provided, and writing clarity.',
              timeLimit: 35,
              wordLimit: 750
            }
          }
        },
        {
          id: 'lesson-4',
          title: 'Motivation and Communication Goals',
          content: 'Understand how different motivational factors drive communication behaviors and goal achievement. This lesson examines intrinsic vs. extrinsic motivation in communication contexts, self-determination theory applications, achievement motivation and communication performance, and social motivation factors affecting interpersonal communication choices.',
          duration: '75 minutes',
          readingMaterials: [
            'Motivation Theory in Communication Contexts',
            'Self-Determination and Communication Competence'
          ],
          keyTerms: [
            'Intrinsic Motivation',
            'Self-Determination Theory',
            'Achievement Motivation',
            'Social Motivation',
            'Goal-Oriented Communication'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'interpersonal-skills',
              prompt: 'Identify and practice different motivational communication strategies for helping someone achieve a personal goal while respecting their autonomy.',
              scenario: 'A friend is struggling with motivation to complete a challenging academic program',
              timeLimit: 20
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Discussion Forum',
          title: 'Cognitive Bias in Daily Communication',
          points: 25,
          dueWeek: 2
        },
        {
          type: 'Personality Assessment Analysis',
          title: 'Personal Communication Style Reflection',
          points: 50,
          dueWeek: 3
        }
      ]
    },
    {
      id: 'module-2',
      title: 'Social Psychology and Group Communication',
      description: 'Examine how social factors, group dynamics, and cultural influences shape communication patterns and effectiveness',
      weeks: [4, 5, 6],
      learningOutcomes: [
        'Analyze social influence processes in communication contexts',
        'Understand group dynamics and their impact on communication patterns',
        'Evaluate cultural factors affecting communication styles and interpretations',
        'Apply social psychology theories to improve group communication effectiveness'
      ],
      lessons: [
        {
          id: 'lesson-5',
          title: 'Social Influence and Conformity',
          content: 'Explore how social pressure, conformity, and compliance mechanisms affect communication behavior in various contexts. This lesson covers classical studies in social influence (Asch, Milgram, Zimbardo), contemporary research on social proof and authority in digital communication, minority influence and innovation in group settings, and resistance strategies for maintaining authentic communication under social pressure.',
          duration: '90 minutes',
          readingMaterials: [
            'Social Influence: Science and Practice - Chapters 2-4',
            'Classic Studies in Social Psychology and Communication',
            'Digital Age Conformity and Social Media Influence'
          ],
          keyTerms: [
            'Social Proof',
            'Authority Principle',
            'Compliance Techniques',
            'Minority Influence',
            'Normative Social Influence',
            'Informational Social Influence',
            'Reactance Theory'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'interpersonal-skills',
              prompt: 'Demonstrate resistance to group pressure while maintaining positive relationships. Practice assertive communication techniques in a group decision-making scenario.',
              scenario: 'Your project team wants to make a decision you strongly disagree with, but you need to maintain working relationships',
              timeLimit: 20,
              evaluationCriteria: ['Assertiveness without aggression', 'Relationship maintenance', 'Clear reasoning', 'Respectful disagreement']
            }
          }
        },
        {
          id: 'lesson-6',
          title: 'Group Dynamics and Communication Patterns',
          content: 'Analyze how group roles, norms, and structures influence communication within teams and organizations. This comprehensive lesson examines group formation stages and communication evolution, role theory and communication expectations, power dynamics and communication hierarchies, and decision-making processes and communication patterns. Students will learn about groupthink prevention and fostering diverse perspectives in group communication.',
          duration: '90 minutes',
          readingMaterials: [
            'Group Communication Theory and Practice',
            'Power Dynamics in Organizational Communication',
            'Decision-Making and Group Communication Patterns'
          ],
          keyTerms: [
            'Group Formation Stages',
            'Role Theory',
            'Power Distance',
            'Groupthink',
            'Social Loafing',
            'Group Polarization',
            'Communication Networks'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'public-speaking',
              prompt: 'Lead a group discussion about a controversial workplace policy, managing different viewpoints and ensuring everyone participates effectively.',
              criteria: ['Facilitation skills demonstration', 'Conflict management', 'Inclusive communication techniques', 'Group engagement maintenance'],
              timeLimit: 30,
              groupSize: '5-6 participants'
            }
          }
        },
        {
          id: 'lesson-7',
          title: 'Cultural Psychology and Communication',
          content: 'Examine cross-cultural differences in communication styles, values, and psychological patterns that affect intercultural interactions. This lesson explores individualistic vs. collectivistic communication patterns, high-context vs. low-context cultural communication styles, cultural dimensions theory and communication implications, and stereotype threat and cross-cultural communication competence development.',
          duration: '90 minutes',
          readingMaterials: [
            'Cross-Cultural Communication Psychology',
            'Cultural Dimensions and Communication Patterns',
            'Intercultural Competence Development'
          ],
          keyTerms: [
            'Cultural Dimensions Theory',
            'High-Context vs. Low-Context',
            'Individualism-Collectivism',
            'Power Distance',
            'Uncertainty Avoidance',
            'Cultural Intelligence',
            'Stereotype Threat'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'written-communication',
              prompt: 'Compare and contrast communication styles between two different cultures, analyzing psychological and cultural factors that contribute to differences. Include specific examples and avoid stereotyping.',
              rubric: 'Assess cultural understanding depth, psychological analysis accuracy, comparison thoroughness, respectful representation, and specific examples.',
              timeLimit: 40,
              wordLimit: 1000
            }
          }
        },
        {
          id: 'lesson-8',
          title: 'Social Identity and Communication',
          content: 'Understand how social identity, in-group/out-group dynamics, and identity threat affect communication patterns and intergroup relations. This lesson covers social identity theory applications, communication accommodation theory, stereotype threat in communication contexts, and identity-safe communication strategies.',
          duration: '75 minutes',
          readingMaterials: [
            'Social Identity Theory and Communication',
            'Communication Accommodation Theory',
            'Identity and Intergroup Communication'
          ],
          keyTerms: [
            'Social Identity Theory',
            'In-group/Out-group Bias',
            'Communication Accommodation',
            'Identity Threat',
            'Intersectionality',
            'Social Categories'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'interpersonal-skills',
              prompt: 'Practice identity-safe communication when discussing topics that might trigger identity threat or defensiveness.',
              scenario: 'Discussing workplace diversity initiatives with colleagues from different backgrounds',
              timeLimit: 25
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Case Study Analysis',
          title: 'Group Communication Breakdown Analysis',
          points: 75,
          dueWeek: 5
        },
        {
          type: 'Cultural Communication Research Project',
          title: 'Cross-Cultural Communication Comparison',
          points: 100,
          dueWeek: 6
        }
      ]
    },
    {
      id: 'module-3',
      title: 'Persuasion and Attitude Change',
      description: 'Explore psychological theories of persuasion, attitude formation, and behavior change through strategic communication',
      weeks: [7, 8, 9],
      learningOutcomes: [
        'Master major theories of persuasion and attitude change',
        'Analyze persuasive messages using psychological frameworks',
        'Understand resistance to persuasion and ethical considerations',
        'Design effective and ethical persuasive communication strategies'
      ],
      lessons: [
        {
          id: 'lesson-9',
          title: 'Theories of Persuasion and Attitude Change',
          content: 'Comprehensive examination of major persuasion theories including the Elaboration Likelihood Model, Heuristic-Systematic Model, and Social Cognitive Theory. This lesson explores central vs. peripheral route processing, systematic vs. heuristic processing, attitude accessibility and strength, and the role of motivation and ability in persuasion. Students will learn to apply these frameworks to analyze real-world persuasive communications.',
          duration: '90 minutes',
          readingMaterials: [
            'Persuasion: Theory and Research - Chapters 3-5',
            'Elaboration Likelihood Model: Current Status and Controversies',
            'Social Cognitive Theory Applications in Persuasion'
          ],
          keyTerms: [
            'Elaboration Likelihood Model',
            'Central Route Processing',
            'Peripheral Route Processing',
            'Heuristic-Systematic Model',
            'Attitude Accessibility',
            'Attitude Strength',
            'Need for Cognition'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'media-analysis',
              prompt: 'Analyze a persuasive advertisement using the Elaboration Likelihood Model. Identify central and peripheral route processing cues, assess target audience motivation and ability, and evaluate persuasive effectiveness.',
              criteria: ['Theory application accuracy', 'Cue identification completeness', 'Processing route analysis depth', 'Effectiveness assessment reasoning'],
              timeLimit: 30,
              requiredElements: ['Central route cues', 'Peripheral route cues', 'Target audience analysis', 'Effectiveness prediction']
            }
          }
        },
        {
          id: 'lesson-10',
          title: 'Cognitive Dissonance and Attitude Change',
          content: 'Explore how inconsistency between beliefs, attitudes, and behaviors creates psychological tension and motivates change. This lesson covers cognitive dissonance theory applications, post-decision dissonance and commitment, effort justification and attitude change, and hypocrisy induction as a behavior change strategy. Students will learn practical applications in health communication, environmental behavior change, and organizational contexts.',
          duration: '90 minutes',
          readingMaterials: [
            'Cognitive Dissonance: 50 Years of Classic Theory',
            'Applications of Dissonance Theory in Health Communication',
            'Hypocrisy and Behavior Change Interventions'
          ],
          keyTerms: [
            'Cognitive Dissonance',
            'Post-Decision Dissonance',
            'Effort Justification',
            'Hypocrisy Induction',
            'Selective Exposure',
            'Consonant Cognitions',
            'Magnitude of Dissonance'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'written-communication',
              prompt: 'Design a comprehensive communication strategy to help someone overcome cognitive dissonance about a health behavior (e.g., smoking, exercise, nutrition). Include theoretical justification and practical implementation steps.',
              rubric: 'Evaluate strategy effectiveness, psychological understanding accuracy, ethical considerations, practical applicability, and theoretical grounding.',
              timeLimit: 35,
              requiredComponents: ['Problem analysis', 'Strategy design', 'Implementation plan', 'Ethical considerations']
            }
          }
        },
        {
          id: 'lesson-11',
          title: 'Resistance to Persuasion and Psychological Reactance',
          content: 'Understand psychological mechanisms people use to resist persuasive attempts and maintain existing beliefs and behaviors. This lesson examines psychological reactance theory, inoculation theory and resistance building, selective exposure and confirmation bias, and forewarning effects on persuasion resistance. Students will learn to recognize and address resistance while respecting autonomy.',
          duration: '90 minutes',
          readingMaterials: [
            'Resistance and Persuasion: Current Perspectives',
            'Psychological Reactance Theory and Applications',
            'Inoculation Theory: Recent Advances and Applications'
          ],
          keyTerms: [
            'Psychological Reactance',
            'Inoculation Theory',
            'Selective Exposure',
            'Confirmation Bias',
            'Forewarning Effects',
            'Counter-arguing',
            'Attitude Bolstering'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'interpersonal-skills',
              prompt: 'Practice ethical persuasion techniques while respecting others\' autonomy and addressing potential resistance constructively. Focus on collaborative problem-solving rather than manipulation.',
              scenario: 'Convincing a friend to consider seeking professional help for a mental health concern they\'ve been dismissing',
              timeLimit: 25,
              evaluationCriteria: ['Respect for autonomy', 'Ethical approach', 'Resistance management', 'Supportive communication']
            }
          }
        },
        {
          id: 'lesson-12',
          title: 'Social Proof and Authority in Persuasion',
          content: 'Examine how social influence principles operate in persuasive communication, including social proof, authority, and consensus mechanisms. This lesson covers bandwagon effects and social validation, expert vs. trustworthy sources, celebrity endorsements and credibility, and social media influence and viral persuasion.',
          duration: '75 minutes',
          readingMaterials: [
            'Social Influence Principles in Modern Persuasion',
            'Authority and Credibility in Digital Communication',
            'Viral Persuasion and Social Media Psychology'
          ],
          keyTerms: [
            'Social Proof',
            'Authority Principle',
            'Source Credibility',
            'Bandwagon Effect',
            'Viral Persuasion',
            'Influencer Psychology'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'digital-storytelling',
              prompt: 'Create a social media campaign that ethically uses social proof and authority principles to promote a positive behavior change.',
              elements: ['Clear behavior change goal', 'Ethical social proof use', 'Credible authority sources', 'Engagement strategy'],
              timeLimit: 40
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Persuasion Analysis Project',
          title: 'Multi-Media Persuasion Analysis',
          points: 125,
          dueWeek: 8
        },
        {
          type: 'Persuasive Message Design',
          title: 'Ethical Persuasion Campaign Development',
          points: 100,
          dueWeek: 9
        }
      ]
    },
    {
      id: 'module-4',
      title: 'Psychological Barriers and Communication Challenges',
      description: 'Explore psychological obstacles to effective communication and evidence-based strategies for overcoming them',
      weeks: [10, 11, 12],
      learningOutcomes: [
        'Identify psychological barriers that impede effective communication',
        'Understand anxiety, conflict, and mental health impacts on communication',
        'Develop strategies for supportive and therapeutic communication',
        'Apply psychological principles to overcome communication challenges'
      ],
      lessons: [
        {
          id: 'lesson-13',
          title: 'Communication Anxiety and Apprehension',
          content: 'Comprehensive examination of communication anxiety, its psychological roots, and evidence-based management strategies. This lesson covers communication apprehension theory and measurement, social anxiety and public speaking fears, physiological and cognitive components of communication anxiety, and cognitive-behavioral interventions for anxiety reduction. Students will learn practical techniques for managing their own and others\' communication anxiety.',
          duration: '90 minutes',
          readingMaterials: [
            'Communication Apprehension: Research and Clinical Perspectives',
            'Cognitive-Behavioral Approaches to Communication Anxiety',
            'Social Anxiety and Interpersonal Communication'
          ],
          keyTerms: [
            'Communication Apprehension',
            'Social Anxiety Disorder',
            'Performance Anxiety',
            'Systematic Desensitization',
            'Cognitive Restructuring',
            'Exposure Therapy',
            'Relaxation Techniques'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'public-speaking',
              prompt: 'Deliver a 4-minute speech about overcoming communication fears while demonstrating relaxation and confidence-building techniques. Include personal strategies and research-based recommendations.',
              criteria: ['Anxiety management demonstration', 'Content accuracy and depth', 'Technique application', 'Authenticity and vulnerability'],
              timeLimit: 15,
              preparationTime: 10
            }
          }
        },
        {
          id: 'lesson-14',
          title: 'Conflict Psychology and Aggressive Communication',
          content: 'Analyze psychological factors that contribute to conflict escalation and aggressive communication patterns. This lesson examines frustration-aggression hypothesis, social learning theory and aggressive modeling, attribution theory in conflict situations, and de-escalation techniques based on psychological principles. Students will practice conflict resolution and assertive communication skills.',
          duration: '90 minutes',
          readingMaterials: [
            'Psychology of Conflict and Aggression',
            'De-escalation Techniques: Theory and Practice',
            'Assertive vs. Aggressive Communication Patterns'
          ],
          keyTerms: [
            'Frustration-Aggression Hypothesis',
            'Social Learning Theory',
            'Attribution Errors',
            'Fundamental Attribution Error',
            'De-escalation Techniques',
            'Assertive Communication',
            'Conflict Styles'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'interpersonal-skills',
              prompt: 'Practice de-escalation techniques when someone becomes verbally aggressive. Focus on psychological principles of calming and validation while maintaining boundaries.',
              scenario: 'A customer service situation where a client is extremely angry about a billing error and is using hostile language',
              timeLimit: 20,
              evaluationCriteria: ['Calming techniques', 'Boundary maintenance', 'Validation skills', 'Problem-solving focus']
            }
          }
        },
        {
          id: 'lesson-15',
          title: 'Mental Health and Communication Patterns',
          content: 'Understand how various mental health conditions affect communication patterns and develop strategies for supportive, sensitive communication. This lesson covers depression and communication withdrawal, anxiety disorders and social communication, trauma responses and communication patterns, and stigma reduction through informed communication. Students will learn about psychological first aid and supportive communication techniques.',
          duration: '90 minutes',
          readingMaterials: [
            'Mental Health and Communication Research',
            'Trauma-Informed Communication Approaches',
            'Supportive Communication in Mental Health Contexts'
          ],
          keyTerms: [
            'Communication Withdrawal',
            'Social Anxiety Impact',
            'Trauma-Informed Communication',
            'Psychological First Aid',
            'Stigma and Communication',
            'Supportive Communication',
            'Active Listening'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'written-communication',
              prompt: 'Write comprehensive guidelines for communicating supportively with someone experiencing depression, incorporating current psychological research and ethical considerations.',
              rubric: 'Assess psychological accuracy, sensitivity and empathy, practical applicability, ethical considerations, and research integration.',
              timeLimit: 30,
              wordLimit: 800
            }
          }
        },
        {
          id: 'lesson-16',
          title: 'Addiction Psychology and Communication',
          content: 'Explore how addiction affects communication patterns and relationships, and learn supportive communication strategies for individuals and families affected by addiction. This lesson covers addiction psychology basics, communication patterns in addiction contexts, motivational interviewing techniques, and family communication dynamics in addiction recovery.',
          duration: '75 minutes',
          readingMaterials: [
            'Addiction Psychology and Communication',
            'Motivational Interviewing: Theory and Practice',
            'Family Systems and Addiction Communication'
          ],
          keyTerms: [
            'Addiction Psychology',
            'Motivational Interviewing',
            'Stages of Change Model',
            'Enabling Communication',
            'Codependency',
            'Recovery Communication'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'interpersonal-skills',
              prompt: 'Practice motivational interviewing techniques to support someone considering behavior change without being confrontational or judgmental.',
              scenario: 'A family member struggling with alcohol use who is ambivalent about seeking help',
              timeLimit: 25
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Case Study Analysis',
          title: 'Communication Barrier Assessment and Intervention Plan',
          points: 100,
          dueWeek: 11
        },
        {
          type: 'Skills Demonstration',
          title: 'Supportive Communication Techniques Video',
          points: 75,
          dueWeek: 12
        }
      ]
    },
    {
      id: 'module-5',
      title: 'Applied Communication Psychology in Professional Contexts',
      description: 'Practical applications of communication psychology principles in various professional and therapeutic settings',
      weeks: [13, 14, 15],
      learningOutcomes: [
        'Apply communication psychology in therapeutic and helping relationships',
        'Understand organizational communication psychology and leadership applications',
        'Evaluate emerging trends in digital communication psychology',
        'Develop expertise in specialized communication psychology applications'
      ],
      lessons: [
        {
          id: 'lesson-17',
          title: 'Therapeutic Communication and Helping Relationships',
          content: 'Examine psychological principles underlying effective helping relationships and therapeutic communication across various contexts. This lesson covers Carl Rogers\' person-centered approach, empathetic communication and unconditional positive regard, motivational interviewing and behavior change communication, and crisis communication and psychological first aid. Students will practice therapeutic communication skills and learn ethical boundaries.',
          duration: '90 minutes',
          readingMaterials: [
            'Person-Centered Counseling and Communication',
            'Therapeutic Communication Skills in Practice',
            'Ethics in Helping Relationships and Communication'
          ],
          keyTerms: [
            'Person-Centered Approach',
            'Unconditional Positive Regard',
            'Empathetic Understanding',
            'Therapeutic Relationship',
            'Motivational Interviewing',
            'Crisis Communication',
            'Boundary Setting'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'interpersonal-skills',
              prompt: 'Practice active listening and empathetic responding techniques while maintaining appropriate boundaries. Demonstrate psychological safety creation and validation skills.',
              scenario: 'A colleague sharing stress about work-life balance and feeling overwhelmed with personal responsibilities',
              timeLimit: 25,
              evaluationCriteria: ['Active listening demonstration', 'Empathy expression', 'Boundary maintenance', 'Validation skills']
            }
          }
        },
        {
          id: 'lesson-18',
          title: 'Organizational Communication Psychology and Leadership',
          content: 'Apply psychological principles to workplace communication, leadership effectiveness, and organizational culture development. This lesson examines psychological safety in teams, transformational vs. transactional leadership communication, organizational change communication and resistance management, and diversity, equity, and inclusion communication strategies. Students will learn to create psychologically safe communication environments.',
          duration: '90 minutes',
          readingMaterials: [
            'Psychological Safety and Team Performance',
            'Leadership Communication Psychology',
            'Organizational Change and Communication Psychology'
          ],
          keyTerms: [
            'Psychological Safety',
            'Transformational Leadership',
            'Organizational Culture',
            'Change Communication',
            'Team Dynamics',
            'Inclusive Communication',
            'Performance Communication'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'digital-storytelling',
              prompt: 'Create a comprehensive training module about building psychological safety in workplace communication, including real scenarios, strategies, and assessment tools.',
              elements: ['Psychological safety concepts', 'Practical implementation strategies', 'Engaging workplace scenarios', 'Clear learning objectives', 'Assessment methods'],
              timeLimit: 45,
              deliverable: 'Interactive training presentation'
            }
          }
        },
        {
          id: 'lesson-19',
          title: 'Digital Communication Psychology and Technology',
          content: 'Explore emerging research in digital communication psychology, AI interaction, and evolving communication patterns in technological contexts. This lesson covers social media psychology and communication behavior, online disinhibition effect and digital communication patterns, virtual reality and telepresence communication psychology, and AI-human interaction and communication implications.',
          duration: '90 minutes',
          readingMaterials: [
            'Digital Communication Psychology: Current Research',
            'Social Media and Human Behavior',
            'AI-Human Interaction and Communication Psychology'
          ],
          keyTerms: [
            'Online Disinhibition Effect',
            'Digital Communication Competence',
            'Virtual Presence',
            'AI Communication Psychology',
            'Social Media Psychology',
            'Digital Empathy',
            'Technological Communication Barriers'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'public-speaking',
              prompt: 'Present a comprehensive 6-minute analysis of how technology is changing the psychology of human communication, including both positive and negative implications for the future.',
              criteria: ['Research integration quality', 'Future predictions reasoning', 'Psychological insight depth', 'Compelling presentation delivery', 'Critical analysis'],
              timeLimit: 20,
              preparationTime: 15
            }
          }
        },
        {
          id: 'lesson-20',
          title: 'Specialized Applications and Future Directions',
          content: 'Examine specialized applications of communication psychology in healthcare, education, legal settings, and emerging fields. This lesson covers health communication psychology and patient-provider relationships, educational psychology and learning communication, legal psychology and witness communication, and communication psychology research methods and career paths.',
          duration: '75 minutes',
          readingMaterials: [
            'Health Communication Psychology Applications',
            'Educational Communication and Learning Psychology',
            'Career Paths in Communication Psychology'
          ],
          keyTerms: [
            'Health Communication Psychology',
            'Patient-Provider Communication',
            'Educational Communication',
            'Legal Communication Psychology',
            'Research Methods',
            'Professional Development'
          ],
          interactiveExercise: {
            type: 'communication-interactive',
            data: {
              exerciseType: 'written-communication',
              prompt: 'Design a research proposal investigating a specific aspect of communication psychology that interests you, including methodology and potential applications.',
              rubric: 'Evaluate research question clarity, methodology appropriateness, literature integration, feasibility, and potential impact.',
              timeLimit: 40,
              wordLimit: 1200
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Capstone Project',
          title: 'Applied Communication Psychology Intervention Design',
          points: 150,
          dueWeek: 14
        },
        {
          type: 'Professional Development Portfolio',
          title: 'Communication Psychology Skills Documentation',
          points: 100,
          dueWeek: 15
        }
      ]
    }
  ],
  assignments: [
    {
      id: 'assignment-1',
      title: 'Communication Psychology Case Study Analysis',
      description: 'Conduct an in-depth analysis of a real-world communication situation using multiple psychological theories and principles. Students will observe, document, and analyze communication patterns in a chosen context (workplace, family, social media, etc.) and apply relevant psychological frameworks to explain behaviors and outcomes.',
      instructions: 'Select a communication situation you can observe or have experienced. Document the context, participants, communication patterns, and outcomes. Apply at least three psychological theories to analyze the situation and provide recommendations for improvement.',
      dueWeek: 7,
      points: 100,
      submissionFormat: 'Written report (1500-2000 words) with observation data and theoretical analysis',
      evaluationCriteria: [
        'Observation quality and detail',
        'Appropriate theory application',
        'Analysis depth and insight',
        'Practical recommendations',
        'Writing clarity and organization'
      ]
    },
    {
      id: 'assignment-2',
      title: 'Persuasion Analysis Project',
      description: 'Examine persuasive messages across different media platforms using psychological frameworks. Students will analyze advertisements, political communications, social media campaigns, or public health messages to understand persuasion psychology in action.',
      instructions: 'Choose persuasive messages from at least three different media platforms. Analyze each using persuasion theories studied in class. Compare effectiveness and ethical considerations across different approaches.',
      dueWeek: 10,
      points: 125,
      submissionFormat: 'Multimedia presentation with written analysis (2000-2500 words)',
      evaluationCriteria: [
        'Theory application accuracy',
        'Comparative analysis quality',
        'Ethical evaluation',
        'Evidence support',
        'Presentation quality'
      ]
    },
    {
      id: 'assignment-3',
      title: 'Personal Communication Development Plan',
      description: 'Develop a comprehensive personal communication improvement plan based on psychological principles and self-assessment. Students will identify their communication strengths and challenges, set specific goals, and design evidence-based strategies for improvement.',
      instructions: 'Complete communication assessments, reflect on personal communication patterns, identify areas for growth, and create a detailed development plan with specific strategies and timeline.',
      dueWeek: 13,
      points: 100,
      submissionFormat: 'Written plan (1200-1500 words) with assessment results and implementation timeline',
      evaluationCriteria: [
        'Self-awareness and honesty',
        'Goal specificity and feasibility',
        'Strategy appropriateness',
        'Timeline realism',
        'Commitment to implementation'
      ]
    },
    {
      id: 'assignment-4',
      title: 'Research Paper on Communication Psychology',
      description: 'Conduct original research investigating a specific aspect of communication psychology. Students will design and implement a small-scale study, analyze results, and discuss implications for communication theory and practice.',
      instructions: 'Choose a research question related to communication psychology, design an appropriate methodology, collect and analyze data, and write a research paper following APA format.',
      dueWeek: 15,
      points: 150,
      submissionFormat: 'Research paper (3000-4000 words) in APA format with data analysis and appendices',
      evaluationCriteria: [
        'Research question significance',
        'Methodology appropriateness',
        'Data analysis quality',
        'Discussion and implications',
        'APA format adherence'
      ]
    }
  ],
  finalProject: {
    id: 'final-project',
    title: 'Communication Psychology Application Portfolio',
    description: 'Create a comprehensive portfolio demonstrating mastery of communication psychology principles through practical applications, case studies, and skill demonstrations.',
    requirements: [
      'Personal communication psychology profile',
      'Three detailed case study analyses',
      'Communication intervention design',
      'Skills demonstration videos',
      'Research summary and future learning goals'
    ],
    points: 200,
    dueWeek: 15,
    presentationRequired: true
  },
  finalExam: {
    id: 'final-exam',
    title: 'Comprehensive Communication Psychology Examination',
    description: 'Comprehensive assessment covering psychological theories, research findings, applications, and critical analysis skills in communication psychology.',
    week: 15,
    points: 200,
    timeLimit: 150,
    format: 'Mixed format: multiple choice, short answer, essay questions, and case study analysis',
    sections: [
      {
        title: 'Cognitive and Emotional Processes in Communication',
        points: 50,
        questions: 25
      },
      {
        title: 'Social Psychology and Group Communication',
        points: 50,
        questions: 25
      },
      {
        title: 'Persuasion and Attitude Change',
        points: 50,
        questions: 25
      },
      {
        title: 'Communication Barriers and Mental Health',
        points: 25,
        questions: 15
      },
      {
        title: 'Applied Communication Psychology',
        points: 25,
        questions: 15
      }
    ]
  },
  quizzes: [
    {
      id: 'quiz-1',
      title: 'Foundations and Cognitive Processes Quiz',
      description: 'Assessment of understanding of basic communication psychology concepts and cognitive processes',
      week: 3,
      points: 30,
      timeLimit: 25,
      questions: 20,
      topics: ['Cognitive processes', 'Emotional factors', 'Personality and communication', 'Basic theory application']
    },
    {
      id: 'quiz-2',
      title: 'Social Psychology and Group Dynamics Quiz',
      description: 'Evaluation of social psychology concepts and group communication understanding',
      week: 6,
      points: 35,
      timeLimit: 30,
      questions: 25,
      topics: ['Social influence', 'Group dynamics', 'Cultural communication', 'Social identity']
    },
    {
      id: 'quiz-3',
      title: 'Persuasion and Attitude Change Quiz',
      description: 'Assessment of persuasion theories and attitude change mechanisms',
      week: 9,
      points: 35,
      timeLimit: 30,
      questions: 25,
      topics: ['Persuasion theories', 'Cognitive dissonance', 'Resistance to persuasion', 'Ethical considerations']
    },
    {
      id: 'quiz-4',
      title: 'Communication Barriers and Challenges Quiz',
      description: 'Evaluation of understanding of psychological barriers and intervention strategies',
      week: 12,
      points: 30,
      timeLimit: 25,
      questions: 20,
      topics: ['Communication anxiety', 'Conflict psychology', 'Mental health impacts', 'Supportive communication']
    },
    {
      id: 'quiz-5',
      title: 'Applied Communication Psychology Quiz',
      description: 'Assessment of practical applications and professional contexts',
      week: 14,
      points: 30,
      timeLimit: 25,
      questions: 20,
      topics: ['Therapeutic communication', 'Organizational applications', 'Digital communication', 'Professional development']
    }
  ],
  discussionForums: [
    {
      id: 'forum-1',
      title: 'Cognitive Bias in Everyday Communication',
      description: 'Share examples of cognitive biases affecting communication and discuss strategies for awareness',
      week: 2,
      posts: 3,
      points: 15
    },
    {
      id: 'forum-2',
      title: 'Cultural Communication Experiences',
      description: 'Discuss personal experiences with cross-cultural communication challenges and successes',
      week: 5,
      posts: 3,
      points: 15
    },
    {
      id: 'forum-3',
      title: 'Ethical Persuasion in Modern Media',
      description: 'Analyze current examples of persuasion in media and discuss ethical considerations',
      week: 8,
      posts: 3,
      points: 15
    },
    {
      id: 'forum-4',
      title: 'Supporting Mental Health Through Communication',
      description: 'Share strategies and resources for supportive communication in mental health contexts',
      week: 11,
      posts: 3,
      points: 15
    },
    {
      id: 'forum-5',
      title: 'Future of Communication Psychology',
      description: 'Discuss emerging trends and future directions in communication psychology research and practice',
      week: 14,
      posts: 3,
      points: 15
    }
  ],
  resources: [
    {
      category: 'Required Textbooks',
      items: [
        'Hargie, O. (2019). Skilled Interpersonal Communication: Research, Theory and Practice (6th ed.). Routledge.',
        'Petty, R. E., & Cacioppo, J. T. (2018). Attitudes and Persuasion: Classic and Contemporary Approaches. Westview Press.',
        'Knapp, M. L., Vangelisti, A. L., & Caughlin, J. P. (2020). Interpersonal Communication and Human Relationships (8th ed.). Pearson.'
      ]
    },
    {
      category: 'Recommended Readings',
      items: [
        'Cialdini, R. B. (2021). Influence: The Psychology of Persuasion (Revised Edition). Harper Business.',
        'Goleman, D. (2020). Emotional Intelligence: Why It Matters More Than IQ (25th Anniversary Edition). Bantam.',
        'Sue, D. W., & Sue, D. (2019). Counseling the Culturally Diverse: Theory and Practice (8th ed.). Wiley.',
        'Edmondson, A. (2019). The Fearless Organization: Creating Psychological Safety for Learning, Innovation, and Growth. Wiley.'
      ]
    },
    {
      category: 'Academic Journals',
      items: [
        'Journal of Communication',
        'Communication Research',
        'Personal Relationships',
        'Journal of Social and Personal Relationships',
        'Applied Psychology: Health and Well-Being',
        'Journal of Applied Communication Research'
      ]
    },
    {
      category: 'Online Resources',
      items: [
        'American Psychological Association (APA) Communication Psychology Division',
        'National Communication Association (NCA) Resources',
        'TED Talks on Communication Psychology',
        'Coursera: Psychology of Communication Courses',
        'Research Gate: Communication Psychology Papers'
      ]
    },
    {
      category: 'Professional Organizations',
      items: [
        'National Communication Association (NCA)',
        'International Communication Association (ICA)',
        'American Psychological Association (APA)',
        'Association for Applied and Therapeutic Humor',
        'International Association for the Study of Pain Communication'
      ]
    },
    {
      category: 'Assessment Tools',
      items: [
        'Personal Report of Communication Apprehension (PRCA-24)',
        'Interpersonal Communication Competence Scale',
        'Emotional Intelligence Assessment',
        'Communication Styles Inventory',
        'Conflict Management Assessment'
      ]
    }
  ],
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D': '60-69%',
    'F': 'Below 60%'
  },
  gradingBreakdown: {
    'Quizzes': '20%',
    'Assignments': '30%',
    'Discussion Forums': '10%',
    'Final Project': '20%',
    'Final Exam': '20%'
  },
  attendancePolicy: 'Regular participation in interactive exercises and discussion forums is essential for success in this course. Students are expected to engage actively in all communication practice activities.',
  latePolicy: 'Late assignments will be penalized 5% per day unless prior arrangements are made. Extensions may be granted for documented emergencies or health issues.',
  accommodations: 'Students with documented disabilities that may affect their communication or learning should contact the Office of Disability Services to discuss appropriate accommodations.',
  academicIntegrity: 'All work must be original and properly cited. Plagiarism, cheating, or any form of academic dishonesty will result in course failure. Collaboration is encouraged in discussion forums but individual work must be submitted for all assignments.',
  technologyRequirements: 'Students need reliable internet access for online interactive exercises, video submissions, and virtual participation in communication activities. Recording capabilities for skills demonstration assignments are required.',
  courseEvaluation: 'Student feedback will be collected mid-semester and at course completion to continuously improve the course content and delivery methods.'
};